/**
 * <product-quick-swatch>
 *
 * Renders interactive variant swatches for a product card/tile.
 *
 * Behavior:
 * - Reads embedded JSON from <script type="application/json"> inside the element.
 * - Mirrors Liquid swatch behavior using client-side data.
 * - Tracks Add-to-Cart (CTA) state: "available", "coming_soon", or "out_of_stock".
 *
 * Expected JSON payload:
 * {
 *   "productUrl": "...",
 *   "title": "...",
 *   "optionsWithValues": [{ "name": "Size", "values": ["S","M","L"] }, ...],
 *   "variants": [{
 *     "id": 123,
 *     "title": "M",
 *     "available": true,
 *     "price": 1999,
 *     "compare_at_price": 2499,
 *     "image": { "src": "...", "alt": "..." },
 *     "metafields": { "custom": { "swym_coming_soon": { "value": true } } }
 *   }, ...]
 * }
 */
class ProductQuickSwatch extends HTMLElement {
  connectedCallback() {
    this.productId = this.getAttribute('product-id') || null;

    // Read and validate JSON payload
    this.data = this._readJSON();
    if (
      !this.data ||
      !Array.isArray(this.data.variants) ||
      this.data.variants.length === 0
    ) {
      return;
    }

    // Translation strings
    this.t = {
      quickAdd: window.theme?.locales?.product?.quick_add,
      waitlist: window.theme?.locales?.product?.join_waitlist,
      notify_me: window.theme?.locales?.product?.notify_me,
      oos: window.theme?.locales?.product?.oos,
      view_remaining_options:
        window.theme?.locales?.product.view_remaining_options
    };

    // Compute how many swatches to show before collapsing into "+N"
    let maxDisplay = 5;
    let isSizeOption = false;
    const maxSizeChars = 22;

    // If the option is "Size" and its combined label length is long, show fewer swatches
    const optionSets = this.data.optionsWithValues || [];
    for (const opt of optionSets) {
      const nameLower = String(opt?.name || '').toLowerCase();
      if (nameLower.includes('size')) {
        isSizeOption = true;
        const charSum = (opt?.values || []).reduce(
          (sum, v) => sum + String(v).length,
          0
        );
        if (charSum > maxSizeChars) {
          maxDisplay = 4;
        }
      }
    }

    // Reserve one slot for the "+N" tile when needed
    let remaining = this.data.variants.length - maxDisplay;
    if (remaining >= 0) {
      maxDisplay -= 1; // reserve slot for +N
      remaining += 1; // keep count accurate after reserving
    }

    this._meta = {
      maxDisplay,
      isSizeOption,
      remaining,
      optionCount: 0,
      plusNAdded: false
    };

    // Cache references on the product card/tile
    this.productTile = this.closest('.product-tile') || this.parentElement;
    this.imageEl = this.productTile.querySelector('.product-tile__image img');
    this.priceEl = this.productTile.querySelector('span[data-current-price]');
    this.compareEl = this.productTile.querySelector('span[data-compare-price]');
    this.rbPanel = this.productTile.closest('.routine-builder__panel');

    // Form and CTA container
    this.form = this.productTile.querySelector('form[action="/cart/add"]');
    this.atcContainer = this.productTile.querySelector(
      '.product-tile__quick-add'
    );

    // Default selection: first in-stock variant, otherwise the first variant
    this.selectedVariant =
      this.data.variants.find((v) => v.available) || this.data.variants[0];

    // Render swatches per option group until maxDisplay is reached
    this.innerHTML = '';
    for (let optIndex = 0; optIndex < optionSets.length; optIndex++) {
      if (this._meta.optionCount === this._meta.maxDisplay) break;
      this._renderOption(optionSets[optIndex], optIndex);
    }

    // Apply initial selection state (checked radio, highlight, CTA state)
    this._applyInitialSelection();

    // Re-apply selection after BFCache restores the page
    this._onPageShow = (e) => {
      if (e.persisted) {
        this._applyInitialSelection();
      }
    };
    window.addEventListener('pageshow', this._onPageShow);
  }

  disconnectedCallback() {
    // Clean up BFCache event listener
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow);
      this._onPageShow = null;
    }
  }

  /**
   * read embedded JSON payload
   * @private
   * @returns {Object|null}
   */
  _readJSON() {
    try {
      const scriptEl = this.querySelector('script[type="application/json"]');
      if (scriptEl) {
        return JSON.parse(scriptEl.textContent || '{}');
      } else {
        return null;
      }
    } catch {
      return null;
    }
  }

  /**
   * Reset swatch selection to initial (first in-stock, else first),
   * syncs checked state + highlight + CTA.
   */
  _applyInitialSelection() {
    const inputs = Array.from(this.querySelectorAll('input[name="id"]'));
    const labels = Array.from(this.querySelectorAll('label.quick-add__swatch'));

    // Clear selection
    inputs.forEach((r) => (r.checked = false));
    labels.forEach((l) => l.setAttribute('aria-checked', 'false'));

    // Determine default variant again in case availability changed
    const v =
      this.data.variants.find((x) => x.available) || this.data.variants[0];
    this.selectedVariant = v;

    // Sync the corresponding radio + label
    const input = this.querySelector(
      `input[name="id"][value="${CSS.escape(String(v.id))}"]`
    );
    const label = input ? input.nextElementSibling : null;
    if (input) {
      input.checked = true;
    }
    if (label) {
      label.setAttribute('aria-checked', 'true');
    }

    // Trigger swatch selection flow to update image and CTA
    if (label) {
      this.selectSwatch({ target: label });
    }
  }

  /**
   * Render one option group (e.g., Color / Size) as a <ul> of swatches.
   * Stops when global optionCount hits maxDisplay
   *
   * @private
   * @param {{name:string, values:string[]}} option
   * @param {number} optIndex
   */
  _renderOption(option, optIndex) {
    const optionName = String(option?.name || '');
    const optionNameHandle = optionName.toLowerCase().replace(/\s+/g, '-');
    const values = option?.values || [];

    const ul = document.createElement('ul');
    ul.dataset.maxDisplay = String(this._meta.maxDisplay);
    ul.className = `quick-add-swatches__list ${
      this._meta.isSizeOption
        ? 'quick-add-swatches__list--sizes'
        : 'quick-add-swatches__list--colors'
    }`;

    for (let i = 0; i < values.length; i++) {
      if (this._meta.optionCount === this._meta.maxDisplay) {
        break;
      }

      const value = values[i];

      // Find the variant whose title matches this swatch value
      const variant = this.data.variants.find(
        (v) => String(v.title) === String(value)
      );
      if (!variant) continue;

      this._meta.optionCount++;

      const li = document.createElement('li');

      // Build swatch class names; color swatches also include a value handle
      let classes = ` quick-add__swatch quick-add__swatch--${optionNameHandle}`;
      if (!optionNameHandle.includes('size')) {
        const valueHandle = String(value)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        classes = `swatch-${valueHandle}` + classes;
      }

      // Mark sold-out variants for visual state
      if (!variant.available) {
        classes += ' quick-add__swatch--sold-out';
      }

      // Radio input used to store variant id in the form
      const formId =
        this.form?.getAttribute('id') ||
        `form-${Math.random().toString(36).slice(2, 8)}`;
      const inputId = `${formId}-swatch-${optIndex}-${i}`;

      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'visually-hidden';
      input.id = inputId;
      input.name = 'id';
      input.value = String(variant.id);
      input.tabIndex = -1;

      if (this.selectedVariant && this.selectedVariant.id === variant.id) {
        input.checked = true;
      }

      // Label behaves as the interactive swatch UI
      const label = document.createElement('label');
      label.setAttribute('for', inputId);
      label.className = classes + ' tag-label-responsive';
      label.setAttribute('aria-label', String(value));
      label.tabIndex = 0;

      // Store minimal variant info on dataset for quick reads during selection
      if (variant.available) {
        label.dataset.price = variant.price;
        if (variant.compare_at_price) {
          label.dataset.compareAtPrice = variant.compare_at_price;
        }
      } else {
        label.dataset.price = '';
        label.dataset.compareAtPrice = '';
      }

      label.dataset.variantId = String(variant.id);
      const imgUrl =
        typeof variant.image === 'string' ? variant.image : variant.image?.src;

      if (imgUrl) {
        label.dataset.image = imgUrl;
      }

      if (variant.metafields?.custom?.swym_coming_soon?.value === true) {
        label.dataset.comingSoon = 'true';
      }

      // Use !! to normalize truthy/falsy to a strict boolean
      label.dataset.available = String(!!variant.available);
      label.dataset.productUrl = this.data.productUrl || '#';

      const span = document.createElement('span');
      span.textContent = String(value);
      label.appendChild(span);

      // Click/keyboard interaction selects the variant and updates CTA/image
      const handle = (evt) => {
        input.checked = true;
        this.selectedVariant = variant;
        this.selectSwatch(evt);
      };

      label.addEventListener('click', handle);
      label.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handle(e);
        }
      });

      li.appendChild(input);
      li.appendChild(label);
      ul.appendChild(li);
    }

    // Append a single "+N" link to PDP if there are more options to view
    if (!this._meta.plusNAdded && this._meta.remaining >= 0) {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = this.data.productUrl || '#';
      a.className = 'quick-add__swatch quick-add__swatch--remaining';
      a.title = `${this.t.view_remaining_options} ${this.data.title || 'this product'}`;
      a.tabIndex = 0;
      a.dataset.remainingOptions = `+${this._meta.remaining}`;
      li.appendChild(a);
      ul.appendChild(li);
      this._meta.plusNAdded = true;
    }

    this.appendChild(ul);
  }

  /**
   * Handle swatch selection:
   * - Updates selectedVariant
   * - Determines CTA state (available, oos, waitlist)
   * - Updates product image, CTA button/link, and routine builder (if present)
   *
   * @param {Event} e - click or keyboard event from a swatch label
   */
  selectSwatch(e) {
    const target = e.target.closest('label');
    if (!target) {
      return;
    }

    // Resolve variant from current selection or dataset
    const variant =
      this.selectedVariant ||
      (target.dataset.variantId &&
        this.data.variants.find(
          (x) => String(x.id) === String(target.dataset.variantId)
        ));
    if (!variant) {
      return;
    }

    // Inputs for CTA state machine
    const variantDisabled = !!variant.metafields?.custom?.swym_disabled?.value; // explicit disable flag
    const variantComingSoon =
      !!variant.metafields?.custom?.swym_coming_soon?.value;
    const hasInventory = !!variant.available; // proxy for inventory availability

    // Pull display values from the swatch dataset
    const { price, compareAtPrice, image } = target.dataset;
    const priceText = price;
    const compareAtText = compareAtPrice ? compareAtPrice : '';

    // Swap product image on selection if provided
    if (image && this.imageEl) {
      this.imageEl.src = image;
      this.imageEl.srcset = `${image} 608w`;
    }

    // Update CTA element according to the computed state
    if (!this.atcContainer) {
      return;
    }

    const variantHref = this._variantUrl(variant);
    const productTitle = this.data.title;
    const args = { price: priceText, compareAt: compareAtText, productTitle };

    // "Available" when in stock and not blocked by a metafield.
    const isAvailableCTA = hasInventory && (!variantComingSoon || variantDisabled);
    // "Waitlist" when there is inventory but variant is explicitly marked coming soon
    const isWaitlist = hasInventory && !variantDisabled && variantComingSoon;
    // "Notify me" when no inventory and Swym is not disabled.
    const isNotifyMe = !hasInventory && !variantDisabled;
    // OOS when no inventory and Swym is disabled.
    const isOutOfStock = !hasInventory && variantDisabled;

    let state = 'fallback';

    if (isAvailableCTA) {
      state = 'available';
    } else if (isWaitlist) {
      state = 'waitlist';
    } else if (isNotifyMe) {
      state = 'notify_me';
    } else if (isOutOfStock) {
      state = 'oos';
    }

    switch (state) {
      case 'available': {
        // Ensure a <button type="submit"> exists for ATC
        const btn = this._ensureButton(this.atcContainer);
        if (btn) {
          btn.disabled = false;
          btn.setAttribute('aria-disabled', 'false');
          btn.removeAttribute('data-coming-soon');
          btn.dataset.variantId = String(variant.id);
          this._updateCtaContent(btn, { state: 'available', ...args });
        }
        break;
      }
      case 'oos': {
        // Ensure a link exists to view PDP when out of stock
        const link = this._ensureLink(this.atcContainer, variantHref);
        if (link) {
          link.removeAttribute('data-coming-soon');
          link.setAttribute('aria-disabled', 'false');
          this._updateCtaContent(link, { state: 'oos', ...args });
        }
        break;
      }
      case 'waitlist': {
        // Ensure a link exists and mark it as coming soon
        const link = this._ensureLink(this.atcContainer, variantHref);
        if (link) {
          link.setAttribute('data-coming-soon', 'true');
          link.setAttribute('aria-disabled', 'false');
          this._updateCtaContent(link, { state: 'waitlist', ...args });
        }
        break;
      }
      case 'notify_me': {
        // Ensure a link exists and mark it as notify me.
        const link = this._ensureLink(this.atcContainer, variantHref);
        if (link) {
          link.setAttribute('data-coming-soon', 'true');
          link.setAttribute('aria-disabled', 'false');
          this._updateCtaContent(link, { state: 'notify_me', ...args });
        }
        break;
      }
      default: {
        // Fallback to OOS presentation
        const link = this._ensureLink(this.atcContainer, variantHref);
        if (link) this._updateCtaContent(link, { state: 'oos', ...args });
      }
    }

    // Routine builder integration: update availability class and "Add All" total
    if (this.rbPanel) {
      const previousRadio = this.productTile.querySelector(
        'input[type="radio"]:checked'
      );
      const buyAll = this.rbPanel.querySelector('.add-all');
      const isUnavailable = isOutOfStock || isNotifyMe || isWaitlist;

      this.rbPanel.classList.toggle(
        'routine-builder--unavailable',
        isUnavailable
      );

      // Adjust the "Add All" button total when the selected variant changes
      if (buyAll && previousRadio) {
        const currentValue = buyAll.innerText || '';
        const currentVariants = buyAll.dataset.variantId || '';
        const variantArray = currentVariants ? currentVariants.split(',') : [];

        const currentValueNumber = parseFloat(
          currentValue.replace(/[^0-9.]/g, '')
        );
        if (!isNaN(currentValueNumber)) {
          let previousVariantPrice = 0;
          const previousLabel = previousRadio.nextElementSibling;
          if (previousLabel?.dataset?.price)
            previousVariantPrice = parseFloat(
              previousLabel.dataset.price.replace(/[^0-9.]/g, '')
            );
          if (previousLabel?.dataset?.variantId) {
            const idx = variantArray.indexOf(previousLabel.dataset.variantId);
            if (idx !== -1) {
              variantArray[idx] = String(variant.id);
              buyAll.setAttribute('data-variant-id', variantArray.join(','));
            }
          }
          const selectedPrice = price
            ? parseFloat(price.replace(/[^0-9.]/g, ''))
            : 0;
          const newTotal =
            currentValueNumber - previousVariantPrice + selectedPrice;
          buyAll.innerText = `$${newTotal.toFixed(0).replace(/\.?0+$/, '')} – ADD ALL`;
        }
      }
    }
  }

  /**
   * Replace an element with a new one, preserving aria-label + data-variantId.
   *
   * @private
   * @param {Element} oldEl - element to replace
   * @param {string} newTag - tag name for the replacement (e.g., 'button', 'a')
   * @param {Object} opts   - options for new element
   * @param {string} [opts.className] - classes to apply
   * @param {string} [opts.type]      - button type (only if newTag === 'button')
   * @param {string} [opts.href]      - href (only if newTag === 'a')
   * @param {string} [opts.defaultAria] - fallback aria-label
   * @returns {Element} - the new element
   */
  _swapElement(oldEl, newTag, opts = {}) {
    const el = document.createElement(newTag);
    if (opts.className) {
      el.className = opts.className;
    }
    if (newTag === 'button' && opts.type) {
      el.type = opts.type;
    }
    if (newTag === 'a' && opts.href) {
      el.href = opts.href;
    }

    const aria = oldEl.getAttribute('aria-label');
    el.setAttribute('aria-label', aria || opts.defaultAria || '');

    if (oldEl.dataset.variantId) {
      el.dataset.variantId = oldEl.dataset.variantId;
    }

    oldEl.replaceWith(el);
    return el;
  }

  /**
   * Ensure the CTA inside the container is a <button type="submit">.
   *
   * @private
   * @param {HTMLElement} container - CTA container
   * @returns {HTMLButtonElement|null}
   */
  _ensureButton(container) {
    if (!container) {
      return null;
    }

    const link = container.querySelector('a.button');
    if (link) {
      return this._swapElement(link, 'button', {
        className: 'quick-add__submit button button--secondary',
        type: 'submit',
        defaultAria: 'Add to cart'
      });
    }

    let btn = container.querySelector('button[type="submit"]');
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'submit';
      btn.className = 'quick-add__submit button button--secondary';
      container.appendChild(btn);
    }
    return btn;
  }

  /**
   * Ensure the CTA inside the container is an <a> link.
   *
   * @private
   * @param {HTMLElement} container - CTA container
   * @param {string} href - link target
   * @returns {HTMLAnchorElement|null}
   */
  _ensureLink(container, href) {
    if (!container) {
      return null;
    }

    const btn = container.querySelector('button[type="submit"]');
    if (btn) {
      return this._swapElement(btn, 'a', {
        className: 'quick-add__submit-link button button--secondary',
        href: href || '#',
        defaultAria: 'View product'
      });
    }

    let a = container.querySelector('a.button');
    if (!a) {
      a = document.createElement('a');
      a.className = 'quick-add__submit-link button button--secondary';
      a.href = href || '#';
      container.appendChild(a);
    }
    return a;
  }

  /**
   * Update CTA content (price + label + aria).
   * Ensures element order: price → compare price → divider → label.
   *
   * @private
   * @param {HTMLElement} el - Container element for CTA (button or link).
   * @param {Object} opts
   * @param {'available'|'waitlist'|'oos'} opts.state - State of variant.
   * @param {string} [opts.price] - Formatted current price.
   * @param {string} [opts.compareAt] - Formatted compare-at price.
   * @param {string} [opts.productTitle] - Product title for aria-label.
   */
  _updateCtaContent(el, { state, price, compareAt, productTitle }) {
    if (!el) {
      return;
    }

    // Helper to insert after a reference node
    const insertAfter = (node, ref) => {
      if (ref.nextSibling) {
        el.insertBefore(node, ref.nextSibling);
      } else {
        el.appendChild(node);
      }
    };

    // Ensure label exists
    let label = el.querySelector('.label');
    if (!label) {
      label = document.createElement('span');
      label.className = 'label';
      el.appendChild(label);
    }

    // Ensure price span
    let priceSpan = el.querySelector('span[data-current-price]');
    if (!priceSpan) {
      priceSpan = document.createElement('span');
      priceSpan.setAttribute('data-current-price', '');
    }

    // Ensure compare-at span
    let compareVal = el.querySelector('span[data-compare-price]');
    if (!compareVal) {
      compareVal = document.createElement('span');
      compareVal.className = 'price__value old-price';
      compareVal.setAttribute('data-compare-price', '');
    }

    // Ensure divider
    let sep = el.querySelector('.divider');
    if (!sep) {
      sep = document.createElement('span');
      sep.className = 'divider';
      sep.textContent = '|';
    }

    // Enforce order: price -> compare -> divider -> label
    if (priceSpan !== label.previousSibling) {
      el.insertBefore(priceSpan, label);
    }
    if (compareVal.previousSibling !== priceSpan) {
      insertAfter(compareVal, priceSpan);
    }
    if (sep.previousSibling !== compareVal) {
      insertAfter(sep, compareVal);
    }
    if (label.previousSibling !== sep) {
      insertAfter(label, sep);
    }

    // Helper for aria-label
    const setAria = (text) => {
      el.setAttribute(
        'aria-label',
        [productTitle, state === 'available' && price ? price : null, text]
          .filter(Boolean)
          .join(', ')
      );
    };

    const { quickAdd, waitlist, oos, notify_me } = this.t;

    switch (state) {
      case 'available': {
        // Show price and compare-at (if present), plus "Quick Add" label
        priceSpan.textContent = price || '';
        priceSpan.style.display = price ? '' : 'none';

        if (compareAt) {
          compareVal.style.display = '';
          compareVal.textContent = compareAt;
        } else {
          compareVal.style.display = 'none';
          compareVal.textContent = '';
        }

        sep.style.display = price ? '' : 'none';
        label.textContent = quickAdd;
        setAria(quickAdd);
        break;
      }
      case 'waitlist': {
        // Hide prices and show "Join Waitlist"
        priceSpan.style.display = 'none';
        compareVal.style.display = 'none';
        sep.style.display = 'none';
        label.textContent = waitlist;
        setAria(waitlist);
        break;
      }
      case 'notify_me': {
        // Hide prices and show "Notify me"
        priceSpan.style.display = 'none';
        compareVal.style.display = 'none';
        sep.style.display = 'none';
        label.textContent = notify_me;
        setAria(notify_me);
        break;
      }
      case 'oos':
      default: {
        // Hide prices and show "Out of stock"
        priceSpan.style.display = 'none';
        compareVal.style.display = 'none';
        sep.style.display = 'none';
        label.textContent = oos;
        setAria(oos);
        break;
      }
    }
  }

  /**
   * Check if the product has a specific tag (case-insensitive).
   *
   * @private
   * @param {string} tag - tag name to check
   * @returns {boolean} true if productTags includes the tag
   */
  _productHasTag(tag) {
    if (!tag) {
      return false;
    }

    const tags = this.data.productTags || [];
    const needle = String(tag).trim().toLowerCase();

    return tags.some(
      (t) =>
        String(t || '')
          .trim()
          .toLowerCase() === needle
    );
  }

  /**
   * Build a variant-specific product URL by appending ?variant=ID
   *
   * @private
   * @param {number|string|{id:number|string}} variantOrId
   * @returns {string}
   */
  _variantUrl(variantOrId) {
    const id = typeof variantOrId === 'object' ? variantOrId?.id : variantOrId;

    if (!id) {
      return this.data.productUrl || '#';
    }

    const base = this.data.productUrl || '#';
    return `${base}${base.includes('?') ? '&' : '?'}variant=${id}`;
  }
}

if (!customElements.get('product-quick-swatch')) {
  customElements.define('product-quick-swatch', ProductQuickSwatch);
}
